import { validateNumeric2DArray, validateArray, validateShapesMatch } from '../utils/validation.js';
import { euclideanDistance } from '../utils/matrix.js';
import { mean } from '../utils/stats.js';

export function silhouetteScore(X, labels) {
  if (!Array.isArray(X[0])) {
    X = X.map(val => [val]);
  }

  validateNumeric2DArray(X, 'X');
  validateArray(labels, 'labels');
  validateShapesMatch(X, labels);

  const clusters = Array.from(new Set(labels));
  const n = X.length;

  if (clusters.length <= 1 || clusters.length >= n) {
    return 0;
  }

  const silhouettes = [];

  for (let i = 0; i < n; i++) {
    const currentCluster = labels[i];

    const sameClusterPoints = [];
    const otherClusterDistances = {};

    for (let j = 0; j < n; j++) {
      if (i === j) continue;

      const distance = euclideanDistance(X[i], X[j]);

      if (labels[j] === currentCluster) {
        sameClusterPoints.push(distance);
      } else {
        if (!otherClusterDistances[labels[j]]) {
          otherClusterDistances[labels[j]] = [];
        }
        otherClusterDistances[labels[j]].push(distance);
      }
    }

    const a = sameClusterPoints.length > 0 ? mean(sameClusterPoints) : 0;

    let b = Infinity;
    for (const cluster in otherClusterDistances) {
      const avgDist = mean(otherClusterDistances[cluster]);
      b = Math.min(b, avgDist);
    }

    if (b === Infinity) {
      b = 0;
    }

    const s = a === 0 && b === 0 ? 0 : (b - a) / Math.max(a, b);
    silhouettes.push(s);
  }

  return mean(silhouettes);
}

export function daviesBouldinScore(X, labels) {
  if (!Array.isArray(X[0])) {
    X = X.map(val => [val]);
  }

  validateNumeric2DArray(X, 'X');
  validateArray(labels, 'labels');
  validateShapesMatch(X, labels);

  const clusters = Array.from(new Set(labels));
  const k = clusters.length;

  if (k <= 1) {
    return 0;
  }

  const centroids = {};
  const clusterPoints = {};

  for (const cluster of clusters) {
    const points = X.filter((_, i) => labels[i] === cluster);
    clusterPoints[cluster] = points;

    const nFeatures = points[0].length;
    const centroid = Array(nFeatures).fill(0);

    for (const point of points) {
      for (let j = 0; j < nFeatures; j++) {
        centroid[j] += point[j];
      }
    }

    for (let j = 0; j < nFeatures; j++) {
      centroid[j] /= points.length;
    }

    centroids[cluster] = centroid;
  }

  const scatters = {};
  for (const cluster of clusters) {
    const points = clusterPoints[cluster];
    const centroid = centroids[cluster];

    const distances = points.map(point => euclideanDistance(point, centroid));
    scatters[cluster] = mean(distances);
  }

  let dbIndex = 0;

  for (const i of clusters) {
    let maxRatio = 0;

    for (const j of clusters) {
      if (i === j) continue;

      const centroidDist = euclideanDistance(centroids[i], centroids[j]);
      if (centroidDist === 0) continue;

      const ratio = (scatters[i] + scatters[j]) / centroidDist;
      maxRatio = Math.max(maxRatio, ratio);
    }

    dbIndex += maxRatio;
  }

  return dbIndex / k;
}

export function calinskiHarabaszScore(X, labels) {
  if (!Array.isArray(X[0])) {
    X = X.map(val => [val]);
  }

  validateNumeric2DArray(X, 'X');
  validateArray(labels, 'labels');
  validateShapesMatch(X, labels);

  const n = X.length;
  const clusters = Array.from(new Set(labels));
  const k = clusters.length;

  if (k <= 1 || k >= n) {
    return 0;
  }

  const nFeatures = X[0].length;
  const overallCentroid = Array(nFeatures).fill(0);

  for (const point of X) {
    for (let j = 0; j < nFeatures; j++) {
      overallCentroid[j] += point[j];
    }
  }

  for (let j = 0; j < nFeatures; j++) {
    overallCentroid[j] /= n;
  }

  let betweenClusterDispersion = 0;
  let withinClusterDispersion = 0;

  for (const cluster of clusters) {
    const points = X.filter((_, i) => labels[i] === cluster);
    const nPoints = points.length;

    const centroid = Array(nFeatures).fill(0);
    for (const point of points) {
      for (let j = 0; j < nFeatures; j++) {
        centroid[j] += point[j];
      }
    }
    for (let j = 0; j < nFeatures; j++) {
      centroid[j] /= nPoints;
    }

    betweenClusterDispersion += nPoints * Math.pow(euclideanDistance(centroid, overallCentroid), 2);

    for (const point of points) {
      withinClusterDispersion += Math.pow(euclideanDistance(point, centroid), 2);
    }
  }

  if (withinClusterDispersion === 0) {
    return 0;
  }

  return (betweenClusterDispersion / withinClusterDispersion) * ((n - k) / (k - 1));
}

export function inertia(X, labels, centroids) {
  if (!Array.isArray(X[0])) {
    X = X.map(val => [val]);
  }

  validateNumeric2DArray(X, 'X');
  validateArray(labels, 'labels');
  validateShapesMatch(X, labels);

  let totalInertia = 0;

  for (let i = 0; i < X.length; i++) {
    const centroid = centroids[labels[i]];
    totalInertia += Math.pow(euclideanDistance(X[i], centroid), 2);
  }

  return totalInertia;
}
